"use client";

import { useEffect, useMemo, useState } from "react";
import { Title } from "@/components/common/typography/Title";
import Image, { type StaticImageData } from "next/image";
import { MdiChevronLeft } from "@/assets/icons/mdi-chevron-left";
import { MdiChevronRight } from "@/assets/icons/mdi-chevron-right";

import img05Palier from "@/assets/images/client-mars-2026/img-05-palier.jpg";
import img15Bureau6Postes from "@/assets/images/client-mars-2026/img-15-bureau-6-postes.jpg";
import img23Cafet2em from "@/assets/images/client-mars-2026/img-23-cafet-2em.jpg";
import img24PhoneBox from "@/assets/images/client-mars-2026/img-24-phone-box.jpg";
import img29CafetRdc5 from "@/assets/images/client-mars-2026/img-29-cafet-rdc-5.jpg";
import img30GrandeSdr from "@/assets/images/client-mars-2026/img-30-grande-sdr.jpg";
import img28CafetRdc4 from "@/assets/images/client-mars-2026/img-28-cafet-rdc-4.jpg";
import img06OpenSpace from "@/assets/images/client-mars-2026/img-06-open-space.jpg";
import img12Sdr2 from "@/assets/images/client-mars-2026/img-12-sdr-2.jpg";

const placePhotos = [
  img05Palier,
  img15Bureau6Postes,
  img23Cafet2em,
  img24PhoneBox,
  img29CafetRdc5,
  img30GrandeSdr,
  img28CafetRdc4,
  img06OpenSpace,
  img12Sdr2,
];

export const PlaceSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const viewerPhotos = useMemo<StaticImageData[]>(() => placePhotos, []);

  const openViewer = (index: number) => {
    setActiveIndex(index);
    setIsViewerOpen(true);
  };

  const closeViewer = () => {
    setIsViewerOpen(false);
    setActiveIndex(0);
  };

  const goTo = (index: number) => {
    if (!viewerPhotos.length) return;
    const normalized = (index + viewerPhotos.length) % viewerPhotos.length;
    setActiveIndex(normalized);
  };

  const goPrevious = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  useEffect(() => {
    if (!isViewerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeViewer();
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isViewerOpen, activeIndex, viewerPhotos.length]);

  return (
    <>
      <section className="flex flex-col gap-12 py-20 text-black" id="place">
        <div className="flex flex-col gap-12 px-4 ph:px-24 md:px-48 lg:px-72 xl:px-96">
          <Title title="Le lieu" />
          <div className="w-full ph:w-2/3 md:w-1/2">
            <p>
              Situé à 50 m du métro Cadet, entre la gare Saint-Lazare, la gare
              du Nord et la gare de l&apos;Est. Des bureaux fermés de 3 à 10
              postes, des open spaces, 8 salles de réunion, des phone boxes,
              deux cafétérias, un rooftop et des terrasses répartis sur 7
              étages. Ouvert 24h/24 et 7j/7.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 px-4 ph:px-24 md:grid-cols-2 md:px-48 lg:grid-cols-3 lg:px-72 xl:px-96">
          {placePhotos.map((photo, index) => (
            <button
              type="button"
              key={`place-photo-${index}`}
              onClick={() => openViewer(index)}
              className="relative aspect-[4/3] overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={`Ouvrir la photo ${index + 1} du lieu`}
            >
              <Image
                src={photo}
                alt={`Le lieu - photo ${index + 1}`}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                className="object-cover transition-transform duration-300 hover:scale-[1.03]"
              />
            </button>
          ))}
        </div>
      </section>

      {isViewerOpen && viewerPhotos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-3 sm:p-6"
          onClick={closeViewer}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Galerie Le lieu"
            className="relative w-full max-w-6xl rounded-xl bg-black/95 p-3 text-white sm:p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeViewer}
              aria-label="Fermer la galerie"
              className="absolute right-3 top-3 z-10 rounded-full border border-white/40 px-3 py-1 text-sm hover:bg-white/10"
            >
              Fermer
            </button>

            <div className="mb-3 flex items-center justify-between gap-3 pr-20">
              <p className="truncate text-sm sm:text-base">Le lieu</p>
              <p className="text-xs text-white/80 sm:text-sm">
                {activeIndex + 1} / {viewerPhotos.length}
              </p>
            </div>

            <div className="relative h-[52vh] w-full overflow-hidden rounded-lg sm:h-[68vh]">
              <Image
                src={viewerPhotos[activeIndex]}
                alt={`Le lieu - photo ${activeIndex + 1}`}
                fill
                sizes="100vw"
                className="object-contain"
                priority
              />
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={goPrevious}
                aria-label="Image précédente"
                className="flex items-center gap-1 rounded-lg border border-white/30 px-3 py-2 text-sm hover:bg-white/10"
              >
                <MdiChevronLeft className="h-5 w-5" />
                Précédente
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Image suivante"
                className="flex items-center gap-1 rounded-lg border border-white/30 px-3 py-2 text-sm hover:bg-white/10"
              >
                Suivante
                <MdiChevronRight className="h-5 w-5" />
              </button>
            </div>

            {viewerPhotos.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {viewerPhotos.map((photo, index) => (
                  <button
                    type="button"
                    key={`place-thumb-${index}`}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Voir image ${index + 1}`}
                    className={`relative h-14 w-20 flex-none overflow-hidden rounded-md border ${
                      activeIndex === index
                        ? "border-white"
                        : "border-white/20 opacity-80"
                    }`}
                  >
                    <Image
                      src={photo}
                      alt={`Le lieu miniature ${index + 1}`}
                      fill
                      sizes="80px"
                      className="object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
